import { useRef, type ReactNode } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerItemValue,
    PickerParentSelection,
    PickerSectionData,
    PickerStatus,
} from "@design-system/Picker/Picker.types";
import {
    FILTER_PICKER_LIST_CLASS_NAMES,
    FilterPickerListActions,
    FilterPickerListFrame,
} from "./FilterPickerListParts";

interface FilterPickerListSharedProps<T extends PickerItemValue> {
    value: T[];
    onValueChange: (value: T[]) => void;
    /** Multi-select parents of a tree: their own value (`self`, default) or a toggle for their leaves (`cascade`). */
    parentSelection?: PickerParentSelection;
    /** Replaces the default case-insensitive label match. */
    filter?: (item: PickerItemData<T>, query: string) => boolean;
    /** Every change of the search text, for filters that scope Reverse or a parent toggle to the displayed rows. */
    onInputValueChange?: (query: string) => void;
    /** Tree parents that start expanded besides those holding a selected row; a user collapse still wins. */
    defaultExpandedValues?: readonly T[];
    emptyLabel: string;
    searchPlaceholder?: string;
    /** `loading` swaps the rows for Picker's loading row. */
    status?: PickerStatus;
    /** The footer shows while this is above 0; defaults to `value.length`. */
    selectedCount?: number;
    /** Footer "Deselect (N)"; defaults to `onValueChange([])`. */
    onDeselect?: () => void;
    /** Footer "Reverse"; omitted, the footer has no Reverse button. */
    onReverse?: () => void;
    /** Footer "Apply"; while set, the footer shows even with nothing selected. */
    onApply?: () => void;
    /** Content above the search that does not scroll with the rows (e.g. mode radios). */
    header?: ReactNode;
    /** Caps the scrolling rows and footer (px) for hosts without a fixed-height panel; omitted, the list fills the panel. */
    maxListHeight?: number;
}

type FilterPickerListRows<T extends PickerItemValue> =
    | {
          /** Rows with `children` render as an inline tree. */
          items: ReadonlyArray<PickerItemData<T>>;
          sections?: never;
      }
    | {
          /** Sections render as runs of rows split by separators, under their `title` when set. */
          sections: ReadonlyArray<PickerSectionData<T>>;
          items?: never;
      };

export type FilterPickerListProps<T extends PickerItemValue> =
    FilterPickerListSharedProps<T> & FilterPickerListRows<T>;
/**
 * The filter-panel checkbox list: a search, data-driven rows (flat, in
 * sections or as an inline tree) and the legacy `FilterActionsFooter`
 * (Deselect with the selected count, optional Reverse / Apply) while something
 * is selected, filling the panel's height. Unlike `FilterMultiselect`, whose
 * composed rows take node labels and end in a Reset row, rows here are
 * `PickerItemData` (string labels plus leading / description / rightText),
 * which is what lets them nest into a tree. A `self` parent with a selected
 * descendant reads indeterminate, and Deselect hands focus back to the search.
 */
export const FilterPickerList = <T extends PickerItemValue>({
    items,
    sections,
    value,
    onValueChange,
    parentSelection,
    filter,
    onInputValueChange,
    defaultExpandedValues,
    emptyLabel,
    searchPlaceholder = "Search...",
    status,
    selectedCount = value.length,
    onDeselect = () => onValueChange([]),
    onReverse,
    onApply,
    header,
    maxListHeight,
}: FilterPickerListProps<T>) => {
    const searchRef = useRef<HTMLInputElement>(null);
    // Emptying the selection unmounts the footer, which would drop focus to `<body>`.
    const handleDeselect = () => {
        onDeselect();
        searchRef.current?.focus({ preventScroll: true });
    };

    return (
        <FilterPickerListFrame maxListHeight={maxListHeight}>
            {header}
            <Picker.Select
                inline
                multiple
                searchable
                searchAutoFocus
                searchRef={searchRef}
                items={items}
                sections={sections}
                value={value}
                onValueChange={onValueChange}
                parentSelection={parentSelection}
                indicateSelectedDescendants
                filter={filter}
                onInputValueChange={onInputValueChange}
                defaultExpandedValues={defaultExpandedValues}
                emptyLabel={emptyLabel}
                searchPlaceholder={searchPlaceholder}
                status={status}
                classNames={FILTER_PICKER_LIST_CLASS_NAMES}
                renderFooter={() =>
                    selectedCount > 0 || onApply !== undefined ? (
                        <FilterPickerListActions
                            selectedCount={selectedCount}
                            onDeselect={handleDeselect}
                            onReverse={onReverse}
                            onApply={onApply}
                        />
                    ) : null
                }
            />
        </FilterPickerListFrame>
    );
};
